/**
 * Píldora de estado del módulo e-CF.
 *
 * El proyecto no tiene componente Badge: cada página repite a mano las clases
 * `inline-flex px-2 py-1 text-xs font-medium rounded-full bg-X-100 text-X-800`. Aquí se usa una
 * decena de veces entre estados de la DGII, aprobación comercial y secuencias, así que se
 * centraliza en vez de repetirla. Las clases son exactamente las mismas del resto de la app.
 */

export type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'muted';

const VARIANTS: Record<BadgeVariant, string> = {
  success: 'bg-green-100 text-green-800',
  warning: 'bg-amber-100 text-amber-800',
  danger: 'bg-red-100 text-red-800',
  info: 'bg-blue-100 text-blue-800',
  muted: 'bg-gray-100 text-gray-800',
};

interface EcfBadgeProps {
  variant: BadgeVariant;
  children: React.ReactNode;
  className?: string;
}

export default function EcfBadge({ variant, children, className = '' }: EcfBadgeProps) {
  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${VARIANTS[variant]} ${className}`}
    >
      {children}
    </span>
  );
}

// ─── Diccionarios de estado ──────────────────────────────────────────────────
// Se declaran junto al badge porque los comparten varias pantallas (el panel enlaza a los
// mismos estados que muestran las tablas de emitidos y recibidos).

export const ESTADO_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  pendiente: { label: 'Sin enviar', variant: 'muted' },
  enproceso: { label: 'En proceso', variant: 'warning' },
  aceptado: { label: 'Aceptado', variant: 'success' },
  rechazado: { label: 'Rechazado', variant: 'danger' },
  anulado: { label: 'Anulado', variant: 'muted' },
};

export const COMERCIAL_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  pendiente: { label: 'Sin respuesta', variant: 'muted' },
  aceptado: { label: 'Aprobado', variant: 'success' },
  rechazado: { label: 'Rechazado', variant: 'danger' },
};

export const CERT_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  valid: { label: 'Válido', variant: 'success' },
  uploaded: { label: 'Cargado', variant: 'warning' },
  expired: { label: 'Vencido', variant: 'danger' },
  none: { label: 'Sin cargar', variant: 'muted' },
};

export const AMBIENTE_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  test: { label: 'Pruebas', variant: 'muted' },
  cert: { label: 'Certificación', variant: 'warning' },
  prod: { label: 'Producción', variant: 'success' },
};
